import React from "react";
import { PageHeaderLink } from "component/shared/page-header";
import * as Mui from "@mui/material";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import MuiAlert from "@mui/material/Alert";
import * as Icons from "react-feather";
import { connect } from "react-redux";
import * as Router from "react-router-dom";
import { bindActionCreators, Dispatch } from "redux";
import * as yup from "yup";
import { FullPageCard } from "component/shared/full-page-card";
import { DashboardLayout } from "component/layout/dashboard";
import { MapSearchMapType } from "type/map-search-map-type";
import { FormField, FormSelectField } from "component/shared/form-fields";
import { Tag } from "component/shared/tag";
import { SettingsStickyFooter } from "component/shared/settings-sticky-footer";
import { UnsavedChangesPrompt } from "component/shared/unsaved-changes-prompt";
import { User } from "model/user";
import { saveUser } from "redux/slice/authentication";
import { RootState } from "redux/store";
import { PageProps } from "shared/page-props";
import { validate } from "shared/yup";
import { Map } from "./map";
import { MapPositionType } from "type/map-position";
import { styles } from "./style";

// V10 / V9 copy (Q2 2026 refresh — strings per ticket MAX-3621 / Figma).
const COPY = {
	pageDescriptionV10:
		"Customize how the map appears on your website's search results page, including the default location, zoom level, map type, and position.",
	pageDescriptionV9:
		"Customize how the map appears on your website's search results page, including the default location, zoom level, map type, and experience.",
	zoomLevel:
		"Controls the default zoom level of the map on your search results page. A wider zoom level will display more listings, while a closer zoom level will display fewer.",
	zoomWarning:
		"Note: Only listings within the visible map area will display by default. A large number of listings may impact page performance.",
	mapCenterAddress:
		"Set the default address the map centers on, displaying available listings in that area.",
	mapType: "Choose the default map style displayed on your website's search page.",
	mapPosition:
		"Choose whether the map appears on the left or right side of listing results on your search results page.",
	drawOnMap:
		"Display the \"Draw on Map\" option on your search forms, allowing visitors to draw a custom area to search within.",
	mapHeight:
		"Set the height of the search experience in pixels. If left blank, it will default to full screen height. Note: a fixed height will prevent the navigation menu from remaining visible while scrolling.",
	interactiveLabel: "Interactive Map Search",
	interactiveDescription:
		"A map-based search experience where visitors can explore listings visually. Includes access to filters and search bar.",
	standardLabel: "Standard Map Search",
	standardDescription:
		"A form-based search experience with fields for location, price, property type, and more.",
};

interface Props
	extends PageProps,
		Router.RouteComponentProps,
		ReturnType<typeof mapStateToProps>,
		ReturnType<typeof mapDispatchToProps>,
		WithStyles<typeof styles> {
	user: User;
}

interface State {
	displayDrawOnMap: boolean;
	eurekaEnabled: boolean;
	eurekaSearchHeight: string;
	mapSearchCenterAddress: string;
	mapSearchMapType: MapSearchMapType;
	saving: boolean;
	mapSearchZoom: number;
	mapPosition: string;
	snackbarOpen: boolean;
}

const mapStateToProps = (state: RootState) => {
	return {
		authLoading: state.authentication.loading,
		authError: state.authentication.error,
	};
};

const mapDispatchToProps = (dispatch: Dispatch) =>
	bindActionCreators(
		{
			saveUser,
		},
		dispatch
	);

class Component extends React.Component<Props, State> {
	constructor(props: Props) {
		super(props);
		this.state = this.stateFromUser(props.user);
	}

	// MAX-3621: V9 clients with no saved Eureka preference default to Interactive Map Search
	// (the recommended option); clients with an explicit saved value — and all V10 clients —
	// keep their stored value. Used as the baseline for both initial state and dirty-tracking
	// so the pre-selected default isn't reported as an unsaved change.
	private effectiveEurekaEnabled(user: User): boolean {
		if (!user.kestrelVersion.all && !user.eurekaPreferenceSet) {
			return true;
		}
		return user.eurekaEnabled;
	}

	private stateFromUser(user: User): State {
		return {
			displayDrawOnMap: user.displayDrawOnMap,
			eurekaEnabled: this.effectiveEurekaEnabled(user),
			eurekaSearchHeight: user.eurekaSearchHeight ? user.eurekaSearchHeight.toString() : "",
			mapSearchCenterAddress: user.mapSearchCenterAddress,
			mapSearchMapType: user.mapSearchMapType,
			saving: false,
			mapSearchZoom: user.mapSearchZoom,
			mapPosition: user.mapPosition ? user.mapPosition.id : MapPositionType.LEFT.id,
			snackbarOpen: false,
		};
	}

	private validationSchema = {
		eurekaSearchHeight: yup
			.number()
			.typeError("Value must be a number.")
			.nullable()
			.transform((value, originalValue) => {
				if (originalValue.trim() === "") {
					return null;
				} else {
					return value;
				}
			}),
	};

	private isDirty(): boolean {
		const { user } = this.props;
		const {
			displayDrawOnMap,
			eurekaEnabled,
			eurekaSearchHeight,
			mapSearchCenterAddress,
			mapSearchMapType,
			mapSearchZoom,
			mapPosition,
		} = this.state;
		const userMapPosition = user.mapPosition ? user.mapPosition.id : MapPositionType.LEFT.id;
		return (
			mapSearchZoom !== user.mapSearchZoom ||
			(mapSearchCenterAddress ?? "") !== (user.mapSearchCenterAddress ?? "") ||
			mapSearchMapType.id !== user.mapSearchMapType.id ||
			displayDrawOnMap !== user.displayDrawOnMap ||
			eurekaEnabled !== this.effectiveEurekaEnabled(user) ||
			(parseInt(eurekaSearchHeight) || 0) !== (user.eurekaSearchHeight ?? 0) ||
			mapPosition !== userMapPosition
		);
	}

	private handleSave = () => {
		const { saveUser, user } = this.props;
		const {
			displayDrawOnMap,
			eurekaEnabled,
			eurekaSearchHeight,
			mapSearchCenterAddress,
			mapSearchMapType,
			mapSearchZoom,
			mapPosition,
		} = this.state;
		// Spread the existing user (keeps every other setting intact, e.g. listingDetailModal
		// which now lives on Search > General) and override only the map fields. Avoids leaking
		// component-only state such as `saving` into the PUT payload.
		const updated = {
			...user,
			displayDrawOnMap,
			eurekaEnabled,
			eurekaSearchHeight: parseInt(eurekaSearchHeight) || 0,
			mapSearchCenterAddress,
			mapSearchMapTypeId: mapSearchMapType.id,
			mapSearchZoom,
			mapPosition: MapPositionType.getById(mapPosition),
		};
		saveUser({ user: updated });
		this.setState({ saving: true, snackbarOpen: true });
	};

	private handleCancel = () => {
		this.setState(this.stateFromUser(this.props.user));
	};

	private section(
		title: string,
		description: string | undefined,
		content: React.ReactNode,
		opts?: { divider?: boolean }
	) {
		const { classes = {} } = this.props;
		return (
			<>
				{opts?.divider && <Mui.Divider className={classes.sectionDivider} />}
				<Mui.Box>
					<Mui.Typography variant="h6" className={classes.sectionHeading}>
						{title}
					</Mui.Typography>
					{description && (
						<Mui.Typography variant="body2" className={classes.sectionDescription}>
							{description}
						</Mui.Typography>
					)}
					{content}
				</Mui.Box>
			</>
		);
	}

	public render() {
		const { authError, authLoading, user, classes = {} } = this.props;
		const {
			displayDrawOnMap,
			eurekaEnabled,
			eurekaSearchHeight,
			mapSearchCenterAddress,
			mapSearchMapType,
			saving,
			mapSearchZoom,
			mapPosition,
			snackbarOpen,
		} = this.state;
		const errors = validate(this.validationSchema, this.state);
		const dirty = this.isDirty();
		const isV10 = user.kestrelVersion.all;
		const title = "Map";

		const mapPreview = (
			<Mui.Box className={classes.mapPreview}>
				<Map zoom={mapSearchZoom} address={mapSearchCenterAddress} mapType={mapSearchMapType} />
			</Mui.Box>
		);

		const zoomControl = (
			<>
				<Mui.Box className={classes.zoomWarning}>
					<Icons.AlertTriangle size={16} className={classes.zoomWarningIcon} />
					<Mui.Typography variant="body2">{COPY.zoomWarning}</Mui.Typography>
				</Mui.Box>
				<Mui.Box className={classes.fieldDescriptionGroup}>
					<Mui.Slider
						className={classes.zoomSlider}
						aria-label="Zoom level"
						min={1}
						max={20}
						step={1}
						value={mapSearchZoom}
						valueLabelDisplay="off"
						onChange={(event, value) => this.setState({ mapSearchZoom: value as number })}
					/>
					<Mui.Box className={classes.zoomEndLabels}>
						<Mui.Typography variant="body2">Close</Mui.Typography>
						<Mui.Typography variant="body2">Wide</Mui.Typography>
					</Mui.Box>
				</Mui.Box>
			</>
		);

		const centerAddressControl = (
			<Mui.Box className={classes.fieldDescriptionGroup}>
				<FormField
					label="Search Center Address"
					onChange={(event) => this.setState({ mapSearchCenterAddress: event.target.value })}
					value={mapSearchCenterAddress}
					placeholder="Contact address is used by default"
				/>
			</Mui.Box>
		);

		const mapTypeControl = (
			<Mui.Box className={classes.fieldDescriptionGroup}>
				<FormSelectField
					label="Map Type"
					onChange={(value) =>
						this.setState({ mapSearchMapType: MapSearchMapType.getByIdOrDefault(value) })
					}
					value={mapSearchMapType.id}
					options={MapSearchMapType.values()}
					valueExtractor={(option) => option.id}
					labelExtractor={(option) => option.label}
				/>
			</Mui.Box>
		);

		const mapPositionControl = (
			<Mui.FormControl>
				<Mui.RadioGroup
					name="map-position"
					value={mapPosition}
					onChange={(event) => this.setState({ mapPosition: event.target.value })}
				>
					<Mui.FormControlLabel
						label="Left"
						control={<Mui.Radio color="secondary" />}
						value={"left"}
					/>
					<Mui.FormControlLabel
						label="Right"
						control={<Mui.Radio color="secondary" />}
						value={"right"}
					/>
				</Mui.RadioGroup>
			</Mui.FormControl>
		);

		const isV9 = !isV10;
		// V9 "Map Search Functionality": true = Interactive Map Search (Eureka), false = Standard.
		const interactive = eurekaEnabled;
		const canChooseFunctionality = isV9 && user.permissions.eurekaSearch;
		// Progressive disclosure (V9): Map Type only for Interactive; Draw on Map (polygon)
		// only for Standard; Zoom + Center Address always. V10 keeps Map Type always on.
		const showMapType = isV10 || (isV9 && interactive);
		const showDrawOnMap = isV9 && !interactive && user.permissions.listingPolygonSearch;
		const showMapHeight = isV9 && interactive && user.permissions.eurekaSearch;

		const functionalityControl = (
			<Mui.RadioGroup
				name="map-functionality"
				value={interactive ? "interactive" : "standard"}
				onChange={(event) =>
					this.setState({ eurekaEnabled: event.target.value === "interactive" })
				}
			>
				<Mui.Box className={classes.functionalityOption}>
					<Mui.FormControlLabel
						value="interactive"
						className={classes.controlLabel}
						control={<Mui.Radio color="secondary" className={classes.controlLabelControl} />}
						label={
							<Mui.Box>
								<Mui.Box className={classes.optionTitleRow}>
									<Mui.Typography className={classes.optionTitle}>
										{COPY.interactiveLabel}
									</Mui.Typography>
									<Tag label="Recommended" variant="success" size="small" />
								</Mui.Box>
								<Mui.Typography variant="body2" className={classes.optionDescription}>
									{COPY.interactiveDescription}
								</Mui.Typography>
							</Mui.Box>
						}
					/>
				</Mui.Box>
				<Mui.Box className={classes.functionalityOption}>
					<Mui.FormControlLabel
						value="standard"
						className={classes.controlLabel}
						control={<Mui.Radio color="secondary" className={classes.controlLabelControl} />}
						label={
							<Mui.Box>
								<Mui.Typography className={classes.optionTitle}>
									{COPY.standardLabel}
								</Mui.Typography>
								<Mui.Typography variant="body2" className={classes.optionDescription}>
									{COPY.standardDescription}
								</Mui.Typography>
							</Mui.Box>
						}
					/>
				</Mui.Box>
			</Mui.RadioGroup>
		);

		const drawOnMapControl = (
			<Mui.FormControl>
				<Mui.RadioGroup
					name="draw-on-map"
					value={displayDrawOnMap ? "yes" : "no"}
					onChange={(event) => this.setState({ displayDrawOnMap: event.target.value === "yes" })}
				>
					<Mui.FormControlLabel label="Yes" control={<Mui.Radio color="secondary" />} value="yes" />
					<Mui.FormControlLabel label="No" control={<Mui.Radio color="secondary" />} value="no" />
				</Mui.RadioGroup>
			</Mui.FormControl>
		);

		const mapHeightControl = (
			<Mui.Box className={classes.fieldDescriptionGroup}>
				<FormField
					label="Map Height in px"
					error={!!errors && !!errors.eurekaSearchHeight}
					helperText={errors && errors.eurekaSearchHeight}
					onChange={(event) => this.setState({ eurekaSearchHeight: event.target.value })}
					value={eurekaSearchHeight}
				/>
			</Mui.Box>
		);

		return (
			<DashboardLayout
				permitted={
					user &&
					(user.permissions.listingMapSearch || user.permissions.listingPolygonSearch)
				}
				title={title}
				header={
					<Mui.Box className={classes.headerRow}>
						<Mui.Typography variant="h1">{title}</Mui.Typography>
						{user.baseUrl && (
							<PageHeaderLink href={user.baseUrl}>View Search</PageHeaderLink>
						)}
					</Mui.Box>
				}
			>
				<Mui.Box mb={4}>
					<Mui.Typography variant="body2" className={classes.introText}>
						{isV10 ? COPY.pageDescriptionV10 : COPY.pageDescriptionV9}
					</Mui.Typography>
				</Mui.Box>

				<FullPageCard>
					{this.section("Map Preview", undefined, mapPreview)}

					{canChooseFunctionality &&
						this.section("Map Search Functionality", undefined, functionalityControl, {
							divider: true,
						})}

					{this.section("Zoom Level", COPY.zoomLevel, zoomControl, { divider: true })}
					{this.section("Map Center Address", COPY.mapCenterAddress, centerAddressControl, {
						divider: true,
					})}

					{showMapType &&
						this.section("Map Type", COPY.mapType, mapTypeControl, { divider: true })}

					{isV10 &&
						this.section("Map Position", COPY.mapPosition, mapPositionControl, {
							divider: true,
						})}

					{showDrawOnMap &&
						this.section("Draw on Map", COPY.drawOnMap, drawOnMapControl, { divider: true })}

					{showMapHeight &&
						this.section("Map Height", COPY.mapHeight, mapHeightControl, { divider: true })}
				</FullPageCard>

				<SettingsStickyFooter
					dirty={dirty}
					saving={saving}
					authError={authError}
					onSave={this.handleSave}
					onCancel={this.handleCancel}
				/>

				<Mui.Snackbar
					open={snackbarOpen && !authLoading && !authError}
					message="Settings saved"
					autoHideDuration={6000}
					onClose={() => this.setState({ snackbarOpen: false })}
					anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
				>
					<MuiAlert severity="success" onClose={() => this.setState({ snackbarOpen: false })}>
						Settings saved successfully!
					</MuiAlert>
				</Mui.Snackbar>

				<UnsavedChangesPrompt isDirty={() => this.isDirty()} history={this.props.history} />
			</DashboardLayout>
		);
	}
}

export const RefreshedSettingsMapSearchPage = Router.withRouter(
	withStyles(connect(
		mapStateToProps,
		mapDispatchToProps
	)(Component), styles)
);
